import React, { useState, useEffect } from 'react'
import { useWallet } from '../contexts/WalletContext'
import { 
  MonetizationDashboard, 
  Web3MonetizationHub, 
  Web3SubscriptionPlans,
  TipCreator,
  PayPerView
} from '../components/Monetization'

interface MonetizationStats {
  totalEarnings: number
  monthlyEarnings: number
  totalSubscribers: number
  totalTips: number
  totalPayPerView: number
  conversionRate: number
  avgRevenuePerUser: number
  nftSales: number
  tokenRewards: number
  daoParticipation: number
  crossChainEarnings: number
  defiYield: number
  governanceRewards: number
  totalTransactions: number
  gasFeesPaid: number
  averageTransactionValue: number
  blockchainEfficiency: number
  topEarningContent: Array<{
    id: string
    title: string
    earnings: number
    type: 'subscription' | 'tip' | 'payperview' | 'nft' | 'token' | 'defi'
  }>
}

export default function Monetization() {
  const { address, isConnected } = useWallet()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'subscriptions' | 'tips' | 'payperview'>('dashboard')
  const [stats, setStats] = useState<MonetizationStats>({
    totalEarnings: 2847.32,
    monthlyEarnings: 456.78,
    totalSubscribers: 124,
    totalTips: 234.56,
    totalPayPerView: 567.89,
    conversionRate: 0.087,
    avgRevenuePerUser: 18.45,
    nftSales: 1234.56,
    tokenRewards: 789.12,
    daoParticipation: 456.78,
    crossChainEarnings: 234.56,
    defiYield: 345.67,
    governanceRewards: 123.45,
    totalTransactions: 2847,
    gasFeesPaid: 45.67,
    averageTransactionValue: 12.34,
    blockchainEfficiency: 0.94,
    topEarningContent: [
      { id: '1', title: 'Premium Web3 Course', earnings: 456.78, type: 'subscription' },
      { id: '2', title: 'Exclusive NFT Drop', earnings: 234.56, type: 'nft' },
      { id: '3', title: 'Advanced DeFi Guide', earnings: 123.45, type: 'payperview' },
      { id: '4', title: 'Community Token Airdrop', earnings: 345.67, type: 'token' },
      { id: '5', title: 'Creator Tips', earnings: 89.12, type: 'tip' }
    ]
  })
  const [isLoading, setIsLoading] = useState(false)

  const handleRefresh = async () => {
    setIsLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsLoading(false)
  }

  const handleSubscribe = async (planId: string, amount: number) => {
    console.log('Subscribing to plan:', planId, 'Amount:', amount)
    // Handle subscription logic
  }

  const handleTip = async (amount: number, message?: string) => {
    console.log('Sending tip:', amount, 'Message:', message)
    // Handle tip logic
  }

  const handlePayPerView = async (contentId: string, amount: number) => {
    console.log('Pay per view:', contentId, 'Amount:', amount)
    // Handle pay per view logic
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-gold-500 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🔒</span>
            </div>
            <h1 
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Wallet Required
            </h1>
            <p className="text-purple-200 mb-6 max-w-md mx-auto">
              Connect your wallet to access the monetization dashboard and manage your creator economy
            </p>
            <button className="px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Navigation */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'dashboard', label: '📊 Dashboard', icon: '📊' },
              { id: 'subscriptions', label: '🚀 Subscriptions', icon: '🚀' },
              { id: 'tips', label: '💝 Tips', icon: '💝' },
              { id: 'payperview', label: '👁️ Pay-per-view', icon: '👁️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold shadow-lg'
                    : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'dashboard' && (
          <Web3MonetizationHub
            creatorAddress={address!}
            stats={stats}
            onRefresh={handleRefresh}
            isLoading={isLoading}
          />
        )}

        {activeTab === 'subscriptions' && (
          <Web3SubscriptionPlans
            creatorAddress={address!}
            onSubscribe={handleSubscribe}
            isLoading={isLoading}
          />
        )}

        {activeTab === 'tips' && (
          <div className="max-w-2xl mx-auto">
            <TipCreator
              creatorAddress={address!}
              creatorName="Your Creator Name"
              onTip={handleTip}
              isLoading={isLoading}
              recentTips={[
                {
                  amount: 25,
                  currency: 'SUI',
                  message: 'Amazing content!',
                  timestamp: new Date(),
                  from: '0x123...abc'
                },
                {
                  amount: 50,
                  currency: 'SUI',
                  message: 'Keep up the great work!',
                  timestamp: new Date(Date.now() - 86400000),
                  from: '0x456...def'
                }
              ]}
            />
          </div>
        )}

        {activeTab === 'payperview' && (
          <div className="max-w-4xl mx-auto">
            <PayPerView
              content={[
                {
                  id: '1',
                  title: 'Advanced Web3 Course',
                  description: 'Learn advanced blockchain development techniques',
                  thumbnail: '/api/placeholder/300/200',
                  price: 25,
                  currency: 'SUI',
                  duration: '2 hours',
                  category: 'Education',
                  creator: 'Blockchain Expert',
                  isPurchased: false,
                  previewUrl: '/api/placeholder/video/preview',
                  fullContentUrl: '/api/placeholder/video/full',
                  stats: {
                    views: 1250,
                    likes: 234,
                    purchases: 89,
                    rating: 4.8,
                    reviews: 156
                  }
                },
                {
                  id: '2',
                  title: 'DeFi Yield Farming Guide',
                  description: 'Complete guide to DeFi yield farming strategies',
                  thumbnail: '/api/placeholder/300/200',
                  price: 15,
                  currency: 'SUI',
                  duration: '1.5 hours',
                  category: 'Finance',
                  creator: 'DeFi Guru',
                  isPurchased: true,
                  previewUrl: '/api/placeholder/video/preview',
                  fullContentUrl: '/api/placeholder/video/full',
                  stats: {
                    views: 890,
                    likes: 156,
                    purchases: 234,
                    rating: 4.9,
                    reviews: 89
                  }
                }
              ]}
              onPurchase={handlePayPerView}
              onView={(contentId) => console.log('Viewing content:', contentId)}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  )
}
